import { Button } from '@acpaas-ui/react-components';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReactTooltip from 'react-tooltip';
import { fetchReset } from '../../../../actions/fetch.actions';
import { tableChange } from '../../../../actions/table.actions';
import { MODALS } from '../../../../constants/modals.constants';
import { ALL_SELECT_OPTION } from '../../../../constants/settings.constants';
import { useAdminRouteGuard } from '../../../../hooks/admin/useAdminRouteGuard.hook';
import { useContactsAdvancedSearch } from '../../../../hooks/admin/useContactsAdvancedSearch.hook';
import { useForm } from '../../../../hooks/useForm.hook';
import { useModal } from '../../../../hooks/useModal.hook';
import AddContactModal from '../../../../modals/admin/AddContactModal';
import UpdateContactModal from '../../../../modals/admin/UpdateContactModal';
import DeleteWarningModal from '../../../../modals/DeleteWarningModal';
import { ADMIN_CONTACT_PARTNER_ORGANIZATIONS_RESOURCE } from '../../../../resources/admin/admin.contact.partner.organizations.resource';
import { ADMIN_CONTACT_SCHOOL_ORGANIZATIONS_RESOURCE } from '../../../../resources/admin/admin.contact.school.organizations.resource';
import { ADMIN_CONTACTS_RESOURCE } from '../../../../resources/admin/admin.contacts.resource';
import { tableConfigSelector } from '../../../../selectors/resources.selectors';
import contactsService from '../../../../services/api/contacts.service';
import AdvancedSearch from '../../../core/advanced-search/AdvancedSearch';
import ResourceTable from '../../../core/ResourceTable';
import AdminContactsNotApprovedLegend from './AdminContactsNotApprovedLegend';

const resource = ADMIN_CONTACTS_RESOURCE;

const AdminContacts = () => {
  const dispatch = useDispatch();
  const addContactModal = useModal(MODALS.ADD_CONTACT_MODAL);
  const updateContactModal = useModal(MODALS.UPDATE_CONTACT_MODAL);
  const deleteContactModal = useModal(MODALS.DELETE_CONTACT_MODAL);
  const contactForm = useForm({});
  const [showNotApprovedOnly, setShowNotApprovedOnly] = useState(false);
  const tableConfig = useSelector((state) => tableConfigSelector(state, { resource }));
  const [isFetchingNotApprovedCount, setIsFetchingNotApprovedCount] = useState(false);
  const [hasFetchedNotApprovedCount, setHasFetchedNotApprovedCount] = useState(false);
  const [notApprovedCount, setNotApprovedCount] = useState(0);
  const [notApprovedCountFetchError, setNotApprovedCountFetchError] = useState('');
  // const selected = useSelector((state) => tableSelectedSelector(state, { resource }));
  // const [isApprovingBulk, setIsApprovingBulk] = useState(false);
  const advancedSearch = useContactsAdvancedSearch();

  useAdminRouteGuard();

  const handleContactOpen = (data) => {
    contactForm.load({
      ...data,
      roles: data?.roles?.map((role) => {
        if (role?.entityId) {
          return role;
        }
        return {
          ..._.pick(role, ['isApproved', 'role', 'function', 'treePath', 'id']),
          netName: role?.parents?.netName ?? ALL_SELECT_OPTION.label,
          managementName: role?.parents?.managementName ?? ALL_SELECT_OPTION.label,
          communityName: role?.parents?.communityName ?? ALL_SELECT_OPTION.label,
          schoolName: role?.parents?.schoolName ?? ALL_SELECT_OPTION.label,
          branchName: role?.parents?.branchName ?? ALL_SELECT_OPTION.label,
          organizationName: role?.parents?.organizationName ?? ''
        };
      })
    });
    dispatch(fetchReset(ADMIN_CONTACT_SCHOOL_ORGANIZATIONS_RESOURCE));
    dispatch(fetchReset(ADMIN_CONTACT_PARTNER_ORGANIZATIONS_RESOURCE));
    updateContactModal.onOpen();
  };

  const handleDeleteRequest = () => {
    deleteContactModal.onOpen();
  };

  const handleDeleteConfirm = (onError, onSuccess) => {
    contactsService.remove(contactForm.data.id, {
      onError,
      onSuccess: () => {
        onSuccess();
        updateContactModal.onClose();
        contactForm.load({});
        dispatch(fetchReset(resource));
        dispatch(fetchReset(ADMIN_CONTACT_SCHOOL_ORGANIZATIONS_RESOURCE));
        dispatch(fetchReset(ADMIN_CONTACT_PARTNER_ORGANIZATIONS_RESOURCE));
      }
    });
  };

  const handleShowNotApprovedToggle = () => {
    const value = !showNotApprovedOnly;
    setShowNotApprovedOnly(value);
    const newConfig = { ...tableConfig };
    if (value) {
      newConfig.isApproved = false;
    } else {
      delete newConfig.isApproved;
    }
    dispatch(tableChange(resource, newConfig));
  };

  // const handleApproveBulk = () => {
  //   setIsApprovingBulk(true);
  //   contactsService.approveBulk(selected, {
  //     onError: (message) => {
  //       setIsApprovingBulk(false);
  //       toastService.showToast(TOAST_TYPES.ERROR, message);
  //     },
  //     onSuccess: () => {
  //       setIsApprovingBulk(false);
  //       dispatch(fetchReset(resource));
  //       dispatch(selectedChange(resource, []));
  //       setHasFetchedNotApprovedCount(false);
  //     }
  //   });
  // };

  useEffect(() => {
    if (!isFetchingNotApprovedCount && !hasFetchedNotApprovedCount) {
      setIsFetchingNotApprovedCount(true);
      setNotApprovedCountFetchError('');
      contactsService.getNotApprovedCount({
        onError: (message) => {
          setNotApprovedCountFetchError(message);
          setHasFetchedNotApprovedCount(true);
          setIsFetchingNotApprovedCount(false);
          ReactTooltip.rebuild();
        },
        onSuccess: (count) => {
          setNotApprovedCount(count);
          setHasFetchedNotApprovedCount(true);
          setIsFetchingNotApprovedCount(false);
        }
      });
    }
  }, [hasFetchedNotApprovedCount, isFetchingNotApprovedCount]);

  return (
    <div className="spo-o-admin-contacts">
      <AdvancedSearch className="u-margin-bottom-lg" {...advancedSearch} />
      <ResourceTable
        resource={resource}
        onRowClick={handleContactOpen}
        legend={
          <AdminContactsNotApprovedLegend
            onToggle={handleShowNotApprovedToggle}
            count={notApprovedCount}
            showNotApprovedOnly={showNotApprovedOnly}
            isFetching={isFetchingNotApprovedCount}
            error={notApprovedCountFetchError}
          />
        }
        actions={[
          // selected.length > 0 && (
          //   <Button
          //     key={'contacts-approve'}
          //     iconRight={isApprovingBulk ? 'ai-spin' : 'ai-check-1'}
          //     className="u-margin-right-sm"
          //     size="small"
          //     type="success"
          //     onClick={handleApproveBulk}
          //     disabled={isApprovingBulk}
          //   >
          //     {selected.length} contact{selected?.length > 1 ? 'en' : ''} goedkeuren
          //     {isApprovingBulk && <Spinner className="spo-a-spinner--button" size="small" />}
          //   </Button>
          // ),
          <Button
            key={'contacts-add'}
            iconRight="ai-add"
            className="u-margin-right-sm"
            size="small"
            onClick={addContactModal.onOpen}
          >
            Contact toevoegen
          </Button>
        ].filter((x) => !!x)}
      />
      {addContactModal.isOpen && <AddContactModal {...addContactModal} />}
      {updateContactModal.isOpen && (
        <UpdateContactModal
          {...updateContactModal}
          form={contactForm}
          onDelete={handleDeleteRequest}
          fetchNotApprovedCount={setHasFetchedNotApprovedCount}
        />
      )}
      {deleteContactModal.isOpen && <DeleteWarningModal {...deleteContactModal} onConfirm={handleDeleteConfirm} />}
    </div>
  );
};

export default AdminContacts;
